import * as Sentry from '@sentry/react';
import { redactQueryParams } from '../utils/urlPrivacy';

// Sentry DSN from environment variable
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENABLED = process.env.REACT_APP_SENTRY_ENABLED === 'true';
const ENVIRONMENT = process.env.REACT_APP_SENTRY_ENV;

export const identifyMonitoredUser = (id: number, email: string): void => {
  if (!SENTRY_ENABLED) {
    return;
  }

  Sentry.setUser({ id, email });
};

/**
 * Detach the current user from subsequent Sentry events.
 *
 * Without this, everything reported after a logout stays attributed to whoever
 * was signed in last — including events from the login page and from the next
 * person to use the same browser.
 */
export const clearMonitoredUser = (): void => {
  if (!SENTRY_ENABLED) {
    return;
  }

  Sentry.setUser(null);
};

/**
 * Core's v1 endpoints take the auth token as a URL *path* segment
 * (`/v1/profile/<token>`, `/v1/roles/<token>`), so it cannot be stripped by
 * query-parameter filtering. Un-scrubbed, every breadcrumb and request URL for
 * those calls is a live credential sitting in Sentry.
 */
const TOKEN_IN_PATH = /\/v1\/(profile|roles)\/[^/?#\s]+/gi;

export const scrubTokenFromUrl = (value: string): string =>
  value.replace(TOKEN_IN_PATH, '/v1/$1/[REDACTED]');

/**
 * Query strings carry PHI too: the Referrals and Documents searches put a patient's name in
 * the page URL (`event.request.url`, navigation breadcrumbs) and in the API request URL (XHR
 * breadcrumbs). Every parameter not on the PHI-safe allow-list has its value redacted.
 */
export const scrubQueryFromUrl = (value: string): string => redactQueryParams(value);

/** Both scrubs, for any string that may hold a URL. */
export const scrubUrl = (value: string): string => scrubQueryFromUrl(scrubTokenFromUrl(value));

type SpanJSON = Parameters<NonNullable<Sentry.BrowserOptions['beforeSendSpan']>>[0];

/** Every string value (or string in an array) of a data bag, scrubbed in place. */
const scrubStringValues = (data: Record<string, unknown> | undefined): void => {
  if (!data) {
    return;
  }
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (typeof value === 'string') {
      data[key] = scrubUrl(value);
    } else if (Array.isArray(value)) {
      data[key] = value.map((item) => (typeof item === 'string' ? scrubUrl(item) : item));
    }
  });
};

export const scrubBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => {
  // XHR/fetch breadcrumbs carry `url`; navigation breadcrumbs carry the page URLs as `from` / `to`.
  scrubStringValues(breadcrumb.data);
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = scrubUrl(breadcrumb.message);
  }
  return breadcrumb;
};

/**
 * `browserTracingIntegration` records every XHR/fetch as an `http.client` span whose attributes
 * hold the raw request URL (`url`, `http.url`, `http.query`, `http.fragment`), and whose
 * description keeps the path, Core's `/v1/profile/<token>` included. Resource spans name the
 * resource URL. Every string is scrubbed, not a list of known keys, so a new attribute is covered.
 */
export const scrubSpan = (span: SpanJSON): SpanJSON => {
  if (typeof span.description === 'string') {
    span.description = scrubUrl(span.description);
  }
  scrubStringValues(span.data);
  return span;
};

/** Applied to every event before it leaves the browser. */
export const scrubEvent = <T extends Sentry.Event>(event: T): T => {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  if (typeof event.request?.query_string === 'string') {
    event.request.query_string = scrubUrl(`?${event.request.query_string}`).slice(1);
  }
  // HttpContext adds the page's `Referer` header.
  scrubStringValues(event.request?.headers);
  if (typeof event.transaction === 'string') {
    event.transaction = scrubUrl(event.transaction);
  }
  // Transactions: the root span's attributes and every child span.
  scrubStringValues(event.contexts?.trace?.data);
  event.spans?.forEach(scrubSpan);
  event.exception?.values?.forEach((value) => {
    if (value.value) {
      value.value = scrubUrl(value.value);
    }
  });
  event.breadcrumbs?.forEach(scrubBreadcrumb);
  return event;
};

export const reportMonitoredError = ({
  error,
  tags,
}: {
  error: unknown;
  tags?: Record<string, string>;
}): void => {
  if (!SENTRY_ENABLED) {
    console.error(error);
    return;
  }

  let customError: Error | null = null;

  if (typeof error === 'string') {
    customError = new Error(error);
  } else if (error instanceof Error) {
    customError = error;
  }

  if (customError) {
    Sentry.captureException(customError, tags ? { tags } : undefined);
  }
};

/**
 * Report a session-lifecycle signal.
 *
 * Only ever given coarse, non-identifying facts — a reason string, an HTTP
 * status, and which backend answered. Never a URL (Core puts auth tokens in the
 * path) and never a response body.
 */
export const reportSessionEvent = (
  message: string,
  context: {
    reason?: string;
    status?: number | string | null;
    host?: string | null;
    code?: string | null;
    enforced?: boolean;
  },
): void => {
  if (!SENTRY_ENABLED) {
    console.error(`[session] ${message}`, context);
    return;
  }

  Sentry.captureMessage(message, {
    level: 'warning',
    tags: {
      session_reason: context.reason ?? 'unknown',
      session_status: String(context.status ?? 'none'),
      session_host: context.host ?? 'none',
      session_code: context.code ?? 'none',
      session_enforced: String(context.enforced ?? false),
    },
  });
};

export const initSentry = () => {
  if (!SENTRY_ENABLED || !SENTRY_DSN) {
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    // Off deliberately: this is a HIPAA-covered app. `true` attaches request
    // headers, cookies, and IP addresses to every event.
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Not recording: neither `replaysSessionSampleRate` nor `replaysOnErrorSampleRate` is set.
      // Replay bypasses every scrub hook above, and its recorded `urls` and navigation spans
      // carry the full URL, query included. Enabling it requires a `beforeAddRecordingEvent` that
      // runs `scrubUrl` on those URLs first, and a review of `networkDetailAllowUrls`.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.browserProfilingIntegration(),
      // Errors only. Forwarding console.log/warn from a codebase that logs
      // member phone numbers and call metadata would be a PHI pipeline.
      Sentry.consoleLoggingIntegration({ levels: ['error'] }),
    ],
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,
    beforeSend: (event) => scrubEvent(event),
    // Transactions carry the page URL too (browserTracingIntegration), and their spans carry
    // every request URL. `beforeSendSpan` also covers spans sent on their own (web vitals).
    beforeSendTransaction: (event) => scrubEvent(event),
    beforeSendSpan: (span) => scrubSpan(span),
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
  });
};
